import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    UserCheck, Search, Filter, Eye, CheckCircle, XCircle, Clock,
    FileText, Phone, Mail, MapPin, Star, AlertTriangle, Calendar,
    Loader2, X, Download, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminAxios = () => {
    const token = localStorage.getItem('admin_token');
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
};

export default function AdminProviders() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewAction, setReviewAction] = useState('');
    const [reviewComment, setReviewComment] = useState('');
    const [expiryDates, setExpiryDates] = useState({ government_id: '', registration_certificate: '' });
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

    useEffect(() => {
        fetchProviders();
    }, [statusFilter]);

    const fetchProviders = async () => {
        setLoading(true);
        try {
            const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
            const response = await getAdminAxios().get(`/admin/providers${params}`);
            setProviders(response.data);
        } catch (err) {
            if (err.response?.status === 401) {
                navigate('/admin/login');
            }
            toast.error('Failed to load providers');
        } finally {
            setLoading(false);
        }
    };

    const fetchProviderDetail = async (providerId) => {
        try {
            const response = await getAdminAxios().get(`/admin/providers/${providerId}`);
            setSelectedProvider(response.data);
        } catch (err) {
            toast.error('Failed to load provider details');
        }
    };

    const handleReview = async () => {
        if (!reviewAction) {
            toast.error('Please select an action');
            return;
        }

        setSubmitting(true);
        try {
            await getAdminAxios().post(`/admin/providers/${selectedProvider.id}/review`, {
                action: reviewAction,
                comment: reviewComment,
                document_expiry_dates: reviewAction === 'approve' ? expiryDates : null
            });
            
            toast.success(reviewAction === 'approve' ? 'Provider approved!' : 'Provider rejected');
            setShowReviewModal(false);
            setSelectedProvider(null);
            setReviewComment('');
            setExpiryDates({ government_id: '', registration_certificate: '' });
            fetchProviders();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to process review');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            approved: 'bg-green-500/20 text-green-500',
            pending_review: 'bg-amber-500/20 text-amber-500',
            pending_payment: 'bg-blue-500/20 text-blue-500',
            rejected: 'bg-red-500/20 text-red-500'
        };
        return styles[status] || 'bg-slate-500/20 text-slate-500';
    };

    const getFolderBadge = (folder) => {
        const styles = {
            green: 'bg-green-500/20 text-green-500',
            amber: 'bg-amber-500/20 text-amber-500',
            red: 'bg-red-500/20 text-red-500'
        };
        return styles[folder] || 'bg-slate-500/20 text-slate-500';
    };

    const filteredProviders = providers.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.profession?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white">MediProviders</h1>
                    <p className="text-slate-400">Manage provider applications and compliance</p>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-slate-800 border-slate-700 mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input 
                                placeholder="Search by name, email, profession..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setSearchParams(v !== 'all' ? { status: v } : {}); }}>
                            <SelectTrigger className="w-full sm:w-48 bg-slate-700 border-slate-600 text-white">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="pending_review">Pending Review</SelectItem>
                                <SelectItem value="pending_payment">Pending Payment</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Providers List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                </div>
            ) : filteredProviders.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="py-12 text-center">
                        <UserCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">No providers found</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filteredProviders.map((provider) => (
                        <Card key={provider.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
                            <CardContent className="p-4">
                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                                            {provider.name?.charAt(0) || 'P'}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white">{provider.name}</h3>
                                            <p className="text-sm text-slate-400 capitalize">{provider.profession} • {provider.specialty || 'General'}</p>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                                <span className="flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    {provider.email}
                                                </span>
                                                {provider.phone && (
                                                    <span className="flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {provider.phone}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(provider.status)}`}>
                                            {provider.status?.replace('_', ' ')}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getFolderBadge(provider.folder_status)}`}>
                                            {provider.folder_status} folder
                                        </span>
                                        {provider.ratings?.total_ratings > 0 && (
                                            <span className="flex items-center gap-1 text-amber-500 text-sm">
                                                <Star className="w-4 h-4 fill-amber-500" />
                                                {provider.ratings.overall.toFixed(1)}
                                            </span>
                                        )}
                                        <Button 
                                            size="sm"
                                            variant="outline"
                                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                            onClick={() => fetchProviderDetail(provider.id)}
                                        >
                                            <Eye className="w-4 h-4 mr-1" />
                                            View
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Provider Detail Modal */}
            {selectedProvider && !showReviewModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <Card className="bg-slate-800 border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <CardHeader className="flex flex-row items-start justify-between sticky top-0 bg-slate-800 z-10">
                            <div>
                                <CardTitle className="text-white flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                                        {selectedProvider.name?.charAt(0)}
                                    </div>
                                    {selectedProvider.name}
                                </CardTitle>
                                <CardDescription className="capitalize mt-1">
                                    {selectedProvider.profession} • {selectedProvider.specialty || 'General'}
                                </CardDescription>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setSelectedProvider(null)}>
                                <X className="w-5 h-5 text-slate-400" />
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Status & Compliance */}
                            <div className="flex flex-wrap gap-3">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedProvider.status)}`}>
                                    {selectedProvider.status?.replace('_', ' ')}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getFolderBadge(selectedProvider.folder_status)}`}>
                                    {selectedProvider.folder_status} folder
                                </span>
                            </div>

                            {/* Contact Info */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-700/50 rounded-xl">
                                    <p className="text-xs text-slate-400 mb-1">Email</p>
                                    <p className="text-white">{selectedProvider.email}</p>
                                </div>
                                <div className="p-4 bg-slate-700/50 rounded-xl">
                                    <p className="text-xs text-slate-400 mb-1">Phone</p>
                                    <p className="text-white">{selectedProvider.phone || '-'}</p>
                                </div>
                                <div className="p-4 bg-slate-700/50 rounded-xl">
                                    <p className="text-xs text-slate-400 mb-1">Registration Number</p>
                                    <p className="text-white">{selectedProvider.registration_number}</p>
                                </div>
                                <div className="p-4 bg-slate-700/50 rounded-xl">
                                    <p className="text-xs text-slate-400 mb-1">Regulatory Body</p>
                                    <p className="text-white">{selectedProvider.regulatory_body}</p>
                                </div>
                                <div className="p-4 bg-slate-700/50 rounded-xl">
                                    <p className="text-xs text-slate-400 mb-1">Travel Distance</p>
                                    <p className="text-white">{selectedProvider.travel_distance_km} km</p>
                                </div>
                                <div className="p-4 bg-slate-700/50 rounded-xl">
                                    <p className="text-xs text-slate-400 mb-1">Subscription</p>
                                    <p className="text-white">{selectedProvider.subscription?.plan?.replace('_', ' ')} ({selectedProvider.subscription?.commission_rate}% commission)</p>
                                </div>
                            </div>

                            {/* Documents */}
                            <div>
                                <h4 className="font-medium text-white mb-3">Documents</h4>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {['government_id', 'registration_certificate', 'cv'].map((docType) => {
                                        const doc = selectedProvider.documents?.[docType];
                                        if (!doc) return null;
                                        return (
                                            <div key={docType} className="p-4 bg-slate-700/50 rounded-xl">
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-sm font-medium text-white capitalize">{docType.replace('_', ' ')}</p>
                                                    {doc.verified && <CheckCircle className="w-4 h-4 text-green-500" />}
                                                </div>
                                                <p className="text-xs text-slate-400">{doc.name}</p>
                                                {doc.expiry_date && (
                                                    <p className="text-xs text-amber-500 mt-1">
                                                        Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                                                    </p>
                                                )}
                                                {doc.data && (
                                                    <a 
                                                        href={doc.data} 
                                                        download={doc.name}
                                                        className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 mt-2"
                                                    >
                                                        <Download className="w-3 h-3" />
                                                        Download
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Referees */}
                            {selectedProvider.referees && selectedProvider.referees.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-white mb-3">References</h4>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {selectedProvider.referees.map((ref, idx) => (
                                            <div key={idx} className="p-4 bg-slate-700/50 rounded-xl">
                                                <p className="font-medium text-white">{ref.name}</p>
                                                <p className="text-xs text-slate-400">{ref.relationship}</p>
                                                <p className="text-xs text-slate-400">{ref.email} • {ref.phone}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            {selectedProvider.status === 'pending_review' && (
                                <div className="flex gap-3 pt-4 border-t border-slate-700">
                                    <Button 
                                        className="flex-1 bg-green-600 hover:bg-green-700"
                                        onClick={() => { setReviewAction('approve'); setShowReviewModal(true); }}
                                    >
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Approve
                                    </Button>
                                    <Button 
                                        className="flex-1 bg-red-600 hover:bg-red-700"
                                        onClick={() => { setReviewAction('reject'); setShowReviewModal(true); }}
                                    >
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Reject
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && selectedProvider && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
                        <CardHeader>
                            <CardTitle className="text-white">
                                {reviewAction === 'approve' ? '✅ Approve Provider' : '❌ Reject Provider'}
                            </CardTitle>
                            <CardDescription>{selectedProvider.name}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {reviewAction === 'approve' && (
                                <>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Government ID Expiry Date</Label>
                                        <Input 
                                            type="date"
                                            value={expiryDates.government_id}
                                            onChange={(e) => setExpiryDates({...expiryDates, government_id: e.target.value})}
                                            className="bg-slate-700 border-slate-600 text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Registration Certificate Expiry Date</Label>
                                        <Input 
                                            type="date"
                                            value={expiryDates.registration_certificate}
                                            onChange={(e) => setExpiryDates({...expiryDates, registration_certificate: e.target.value})}
                                            className="bg-slate-700 border-slate-600 text-white"
                                        />
                                    </div>
                                </>
                            )}
                            <div className="space-y-2">
                                <Label className="text-slate-300">Comment (optional)</Label>
                                <Textarea 
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder={reviewAction === 'reject' ? 'Reason for rejection...' : 'Any notes...'}
                                    className="bg-slate-700 border-slate-600 text-white"
                                    rows={3}
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button 
                                    variant="outline"
                                    className="flex-1 border-slate-600 text-slate-300"
                                    onClick={() => setShowReviewModal(false)}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    className={`flex-1 ${reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                                    onClick={handleReview}
                                    disabled={submitting}
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
